// SPDX-License-Identifier: MIT
/**
 * ui-morph (product, 4–8 beats). One interface element in the middle of the frame goes through two
 * to four states, the way a user meets the product: a field is clicked and typed into, it becomes a
 * button, the button is clicked on the beat, and it turns into the result: a card filling with rows,
 * a notification, a badge. The container springs between shapes while its content crossfades inside
 * it, so the viewer follows one object instead of a cut.
 *
 * Every `text` is a claim (it says what the product does) and must come from the brand pack; the
 * rows of a result card are skeleton bars, never invented entries. The pointer only appears when
 * something is clicked: a field, or a button that leads to another state.
 */
import * as z from "zod/mini";
import { clamp, lerp } from '../core/math.ts';
import * as kit from '../kit/index.ts';
import type { Box } from '../kit/path.ts';
import { DESIGN_H, DESIGN_W, type SceneEnv, type SceneTiming, defineScene } from '../scene.ts';
import { chars } from '../type.ts';
import { sceneBeats } from './lib.ts';
const { ui } = kit;
const CX = DESIGN_W / 2;
const CY = DESIGN_H / 2;
/** The element is the hero of the shot: about 1.6× an interface control, drawn at SCALE. */
const H = 104;
const SCALE = 1.3;
const PX = 40;
/** The first state starts here (it grows out of a point just before). */
const START = 0.5;
/** Beats a morph between two states takes. */
const MORPH = 0.55;
/** A field is clicked this long after its state starts (on the half beat). */
const FOCUS = 0.5;
const ICONS = [
    'search',
    'check',
    'plus',
    'arrow',
    'bell',
    'upload',
    'link',
    'chart',
    'user',
    'play',
] as const;
const StateSchema = z.object({
    kind: z.enum(['field', 'button', 'card', 'toast', 'badge']),
    /** What the state says. A field types it in; a card uses it as its title. */
    text: z.optional(z.string().check(z.maxLength(40))),
    /** Skeleton rows of a card (default 3). */
    rows: z.optional(z.number().check(z.int()).check(z.gte(1)).check(z.lte(4))),
    icon: z.optional(z.enum(ICONS)),
});
type State = z.infer<typeof StateSchema>;
const propsSchema = z.object({
    states: z.array(StateSchema).check(z.minLength(2)).check(z.maxLength(4)),
    /** Show the pointer clicking (default true). */
    cursor: z.optional(z.boolean()),
});
type Click = {
    beat: number;
    state: number;
    kind: 'focus' | 'press';
};
type Plan = {
    ranges: Array<[
        number,
        number
    ]>;
    clicks: Click[];
    typing: Array<[
        number,
        number
    ] | null>;
};
const TYPE_CPS = 16;
const readSec = (text = '') => kit.text.readingTime(text);
/**
 * When each state starts and ends, where the clicks fall and when fields are typed. Each state gets
 * what it needs (typing, reading, rows filling in) and the scene's length is shared out in
 * proportion; state changes snap to half beats so clicks land on the music.
 */
function plan(states: State[], timing: SceneTiming): Plan {
    const { beats: L, spb } = timing;
    const sec = (s: number) => s / spb;
    const last = states.length - 1;
    // What each state needs, in beats: its own motion, typing (fields) and time to read it.
    const parts = states.map((s, i) => {
        const read = sec(readSec(s.text));
        switch (s.kind) {
            case 'field':
                return {
                    fixed: FOCUS + 0.05,
                    typing: Math.max(0.5, sec(chars(s.text ?? '').length / TYPE_CPS)),
                    read,
                };
            case 'button':
                return { fixed: 0.5, typing: 0, read: Math.max(1, read) };
            case 'card':
                return { fixed: 0.8 + (s.rows ?? 3) * 0.18, typing: 0, read: Math.max(read, 0.8) };
            default:
                return { fixed: i === last ? 0.4 : 0.6, typing: 0, read };
        }
    });
    const need = (p: (typeof parts)[number]) => p.fixed + p.typing + p.read;
    const sum = () => parts.reduce((a, p) => a + need(p), 0);
    // Short of beats: type faster first (up to twice as fast), so the copy keeps its reading time.
    let excess = sum() - (L - START);
    for (const p of parts) {
        if (excess <= 0)
            break;
        const cut = Math.min(p.typing / 2, excess);
        p.typing -= cut;
        excess -= cut;
    }
    const scale = (L - START) / sum();
    const ranges: Array<[
        number,
        number
    ]> = [];
    let at = START;
    let acc = 0;
    parts.forEach((p, i) => {
        acc += need(p) * scale;
        const end = i === last ? L : Math.max(at + 0.5, Math.round((START + acc) * 2) / 2);
        ranges.push([at, end]);
        at = end;
    });
    const clicks: Click[] = [];
    const typing: Plan['typing'] = [];
    states.forEach((s, i) => {
        const [a, b] = ranges[i]!;
        if (s.kind === 'field') {
            // Clicked on the half beat, typed straight after; never slower than its natural speed.
            clicks.push({ beat: a + FOCUS, state: i, kind: 'focus' });
            const t0 = a + FOCUS + 0.05;
            typing.push([t0, Math.min(b - 0.3, t0 + parts[i]!.typing * Math.min(1, scale))]);
        }
        else
            typing.push(null);
        if (s.kind === 'button' && i < last)
            clicks.push({ beat: b, state: i, kind: 'press' });
    });
    return { ranges, clicks, typing };
}
type Shape = kit.ui.Shape;
/** Natural box of a state, centred in the frame. */
function shapeOf(ctx: CanvasRenderingContext2D, tk: kit.ui.UiTokens, s: State): Shape {
    const text = s.text ?? '';
    ctx.font = tk.font(PX, s.kind === 'field' ? 500 : 600);
    const tw = ctx.measureText(text).width;
    let w: number;
    let h = H;
    let r = tk.radius.md * 1.4;
    let fill = tk.surface;
    switch (s.kind) {
        case 'field':
            w = clamp(tw + 260, 720, 1180);
            fill = tk.sunken;
            break;
        case 'button':
            w = clamp(tw + 150 + (s.icon ? 64 : 0), 300, 1000);
            fill = tk.accent;
            break;
        case 'card':
            w = clamp(tw + 220, 860, 1180);
            h = 150 + (s.rows ?? 3) * 92;
            r = tk.radius.lg * 1.4;
            break;
        case 'toast':
            w = clamp(tw + 260, 640, 1100);
            h = 136;
            r = tk.radius.lg * 1.4;
            fill = tk.raised;
            break;
        case 'badge':
            ctx.font = tk.font(36, 600);
            w = clamp(ctx.measureText(text).width + 150, 260, 900);
            h = 92;
            r = h / 2;
            fill = tk.accentSoft;
            break;
    }
    return { x: CX - w / 2, y: CY - h / 2, w, h, r, fill };
}
/**
 * Pointer path: in from the bottom right, onto each click, then away. It never rests on words: a
 * field is clicked where its text will go and the hand then drops just below the field while the
 * text is typed; a button is pressed in its padding, past the label.
 */
function waypoints(shapes: Shape[], p: Plan, L: number): kit.ui.Waypoint[] {
    if (!p.clicks.length)
        return [];
    const pts: kit.ui.Waypoint[] = [{ beat: 0, x: CX + 640, y: DESIGN_H + 90 }];
    p.clicks.forEach((c, n) => {
        const b = shapes[c.state]!;
        if (c.kind === 'press') {
            pts.push({ beat: c.beat, x: b.x + b.w - 56, y: b.y + b.h * 0.64, click: true });
            return;
        }
        const x = b.x + b.w * 0.3;
        pts.push({ beat: c.beat, x, y: b.y + b.h * 0.62, click: true });
        const next = p.clicks[n + 1]?.beat ?? L;
        if (next - c.beat > 1.2)
            pts.push({ beat: c.beat + 0.75, x: x + 70, y: b.y + b.h + 48 });
    });
    const last = pts[pts.length - 1]!;
    pts.push({ beat: Math.min(L + 0.5, last.beat + 1.5), x: last.x + 300, y: DESIGN_H + 90 });
    return pts;
}
/** How focused a field is: it lights up as the click lands, just before typing starts. */
const focusOf = (env: SceneEnv, typed: [
    number,
    number
] | null) => typed ? kit.beat.seg(env, typed[0] - 0.12, typed[0] + 0.05, kit.curves.glide) : 0;
/** The content of state `s` inside `box` at opacity `a`. */
function content(ctx: CanvasRenderingContext2D, tk: kit.ui.UiTokens, env: SceneEnv, s: State, box: Box, a: number, range: [
    number,
    number
], typed: [
    number,
    number
] | null): void {
    if (a <= 0.002)
        return;
    const prev = ctx.globalAlpha;
    ctx.globalAlpha = prev * a;
    const text = s.text ?? '';
    const cy = box.y + box.h / 2;
    const b0 = range[0];
    switch (s.kind) {
        case 'field': {
            // The container is the field; this draws what is in it.
            const [t0, t1] = typed ?? [b0, b0];
            const ty = ui.typing(env, text, t0, t1, 3);
            const focus = focusOf(env, typed);
            const pad = 40;
            ui.icon(ctx, s.icon ?? 'search', box.x + pad, cy - 22, 44, tk.muted);
            const x0 = box.x + pad + 44 + 24;
            ctx.font = tk.font(PX, 500);
            ui.label(ctx, ty.text, x0, cy + 1, tk.text);
            const w = ctx.measureText(ty.text).width;
            ctx.globalAlpha = prev * a * ty.caret * focus;
            ctx.fillStyle = tk.accent;
            ctx.fillRect(x0 + w + 4, cy - 26, 4, 52);
            break;
        }
        case 'button': {
            // The container is the button; this draws its label. An arrow points onward, after the label;
            // other icons lead.
            const iconW = s.icon ? 64 : 0;
            ui.fitLabel(ctx, tk, text, box.w - 120 - iconW, PX, 600, 28);
            const tw = ctx.measureText(text).width;
            const x = box.x + (box.w - tw - iconW) / 2;
            const trail = s.icon === 'arrow';
            if (s.icon)
                ui.icon(ctx, s.icon, trail ? x + tw + 20 : x, cy - 22, 44, tk.onAccent);
            ui.label(ctx, text, trail ? x : x + iconW, cy + 1, tk.onAccent);
            break;
        }
        case 'card': {
            const pad = 44;
            ui.fitLabel(ctx, tk, text, box.w - 2 * pad, PX, 700, 32);
            ui.label(ctx, text, box.x + pad, box.y + 72, tk.text);
            const rows = s.rows ?? 3;
            for (let i = 0; i < rows; i++) {
                const k = kit.beat.seg(env, b0 + 0.3 + i * 0.18, b0 + 0.8 + i * 0.18, kit.curves.glide);
                if (k <= 0)
                    continue;
                ctx.save();
                ctx.globalAlpha *= k;
                ctx.translate(0, (1 - k) * 18);
                const first = i === 0;
                ui.row(ctx, tk, { x: box.x + 24, y: box.y + 132 + i * 92, w: box.w - 48, h: 80 }, {
                    avatar: i,
                    bars: [0.62 - 0.11 * (i % 3), 0.34 + 0.06 * (i % 2)],
                    selected: first ? kit.beat.seg(env, b0 + 1, b0 + 1.3) : 0,
                    check: first ? kit.beat.seg(env, b0 + 1.2, b0 + 1.6) : 0,
                });
                ctx.restore();
            }
            break;
        }
        case 'toast': {
            const d = 64;
            const ix = box.x + 36;
            ctx.beginPath();
            ctx.arc(ix + d / 2, cy, d / 2, 0, Math.PI * 2);
            ctx.fillStyle = tk.accent;
            ctx.fill();
            ui.icon(ctx, s.icon ?? 'check', ix + 14, cy - 18, 36, tk.onAccent, kit.beat.seg(env, b0 + 0.15, b0 + 0.55));
            const tx = ix + d + 28;
            ui.fitLabel(ctx, tk, text, box.x + box.w - 40 - tx, PX, 600, 28);
            ui.label(ctx, text, tx, cy + 1, tk.text);
            break;
        }
        case 'badge': {
            ctx.font = tk.font(36, 600);
            const w = ctx.measureText(text).width + 52;
            const x = box.x + (box.w - w) / 2;
            ui.icon(ctx, s.icon ?? 'check', x, cy - 18, 36, tk.accentText, kit.beat.seg(env, b0 + 0.1, b0 + 0.5));
            ui.label(ctx, text, x + 52, cy + 1, tk.accentText);
            break;
        }
    }
    ctx.globalAlpha = prev;
}
export default defineScene({
    id: 'ui-morph',
    version: '1.0.0',
    aspects: ['16:9'],
    propsSchema,
    glyphs: (p) => p.states.map((s) => s.text ?? '').join(''),
    claims: (p) => p.states.flatMap((s) => (s.text ? [s.text] : [])),
    motion: () => 0.4,
    audioEvents: (p, timing) => {
        const pl = plan(p.states, timing);
        const ticks = p.cursor === false ? [] : pl.clicks.map((c) => ({ beat: c.beat, sfx: 'tick' as const, gain: 0.7 }));
        // The result arrives with a blip.
        const last = pl.ranges[pl.ranges.length - 1]![0];
        return [...ticks, { beat: last + 0.1, sfx: 'blip' as const, gain: 0.45 }];
    },
    post: (_p, env) => (env.style.background === 'dark' ? { bloom: 0.35, lens: 0 } : { bloom: 0.25, lens: 0 }),
    draw(ctx, props, env) {
        const tk = ui.tokens(env);
        const L = sceneBeats(env);
        ctx.fillStyle = tk.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        if (tk.dark)
            kit.fx.pool(ctx, CX, CY, 900, tk.accent, 0.07);
        kit.camera.zoom(ctx, SCALE * (1 + 0.03 * kit.beat.seg(env, 0, L, kit.curves.silk)), CX, CY);
        const states = props.states;
        const pl = plan(states, { beats: L, spb: env.spb });
        const shapes = states.map((s) => shapeOf(ctx, tk, s));
        const b = env.localBeat;
        let i = 0;
        while (i < states.length - 1 && b >= pl.ranges[i + 1]![0])
            i++;
        const into = i === 0 ? 1 : kit.curves.spring(clamp((b - pl.ranges[i]![0]) / MORPH));
        // The first state grows out of a point just before it starts.
        const grow = kit.curves.spring(clamp((b - (START - 0.4)) / 0.6));
        if (grow <= 0)
            return;
        const dot: Shape = { x: CX - 32, y: CY - 32, w: 64, h: 64, r: 32, fill: shapes[0]!.fill };
        const shape = i === 0 ? ui.morph(dot, shapes[0]!, grow) : ui.morph(shapes[i - 1]!, shapes[i]!, into);
        const cur = props.cursor === false ? null : waypoints(shapes, pl, L);
        const pointer = cur?.length ? ui.cursorAt(env, cur) : null;
        const s = states[i]!;
        const press = s.kind === 'button' ? (pointer?.press ?? 0) : 0;
        // Container: elevation and fill follow the morph; content is clipped to it.
        const lift = s.kind === 'toast' ? 2.2 : s.kind === 'card' ? 1.6 : s.kind === 'badge' ? 0.3 : 1;
        const box = ui.scaleBox(shape, 1 - 0.03 * press);
        // A field's focus ring sits outside it, so it is drawn before the content clip.
        const focus = Math.max(s.kind === 'field' ? focusOf(env, pl.typing[i]!) * (i === 0 ? 1 : into) : 0, i > 0 && states[i - 1]!.kind === 'field' ? 1 - clamp(into / 0.45) : 0);
        if (focus > 0) {
            ctx.save();
            ctx.globalAlpha *= focus;
            ui.rrect(ctx, ui.inset(box, -7), shape.r + 7);
            ctx.lineWidth = 8;
            ctx.strokeStyle = kit.color.mix(tk.accent, tk.bg, 0.55);
            ctx.stroke();
            ctx.restore();
        }
        ui.elevation(ctx, tk, box, shape.r, lerp(1, lift, i === 0 ? 1 : into) * (1 - 0.5 * press));
        ui.rrect(ctx, box, shape.r);
        ctx.fillStyle = s.kind === 'button' ? kit.color.mix(shape.fill, '#000000', 0.14 * press) : shape.fill;
        ctx.fill();
        // Hairline on surfaces, the accent on a focused field.
        if (s.kind !== 'button' && s.kind !== 'badge') {
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = tk.border;
            ctx.stroke();
        }
        if (focus > 0) {
            ctx.save();
            ctx.globalAlpha *= focus;
            ctx.lineWidth = 2.5;
            ctx.strokeStyle = tk.accent;
            ctx.stroke();
            ctx.restore();
        }
        if (s.kind === 'button') {
            // The button's top-edge highlight, as kit.ui.button draws it.
            ctx.save();
            ctx.clip();
            const g = ctx.createLinearGradient(0, box.y, 0, box.y + box.h);
            g.addColorStop(0, `rgba(255,255,255,${0.16 * (1 - press) * into})`);
            g.addColorStop(0.5, 'rgba(255,255,255,0)');
            ctx.fillStyle = g;
            ctx.fillRect(box.x, box.y, box.w, box.h);
            ctx.restore();
        }
        ctx.save();
        ui.rrect(ctx, box, shape.r);
        ctx.clip();
        if (i > 0) {
            const out = 1 - clamp(into / 0.45);
            content(ctx, tk, env, states[i - 1]!, box, out, pl.ranges[i - 1]!, pl.typing[i - 1]!);
        }
        const inA = i === 0 ? clamp((grow - 0.55) / 0.45) : clamp((into - 0.35) / 0.65);
        content(ctx, tk, env, s, box, inA, pl.ranges[i]!, pl.typing[i]!);
        ctx.restore();
        if (pointer) {
            ui.clickRipple(ctx, tk, pointer.clickAt[0], pointer.clickAt[1], pointer.sinceClick);
            ui.cursor(ctx, tk, pointer.x, pointer.y, { press: pointer.press, scale: 1.25 });
        }
    },
});
